import React, { useState, useEffect } from "react";
import { Card, Form, Row, Col } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { uploadImage } from "../../utils/helpers/fileupload";
import { getSubjectTeacher } from "../../store/slices/subjectteacher";
import { adddiarynote } from "../../store/slices/diaryNote";
import { getStudent } from "../../store/slices/student";

import { MdDelete, MdFileCopy, } from "react-icons/md";
import { FcGallery } from "react-icons/fc";

const requiredMessages = {
  standardId: "Please select standard",
  sectionId: "Please select section",
  subjectId: "Please select subject",
  diaryNote: "Please enter description",
};

export default function AddDiaryNote() {
  const user = JSON.parse(localStorage.getItem("user"));
  const token = user?.data?.accessToken;
  const teacherId = user?.data?.teacherId;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [rawData, setRawData] = useState([]);
  const [standards, setStandards] = useState([]);
  const [sections, setSections] = useState([]);
  const [subjects, setSubjects] = useState([]);

  const [students, setStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [studentLoading, setStudentLoading] = useState(false);

  const [errors, setErrors] = useState({});
  const [files, setFiles] = useState([]); 
  const [formData, setFormData] = useState({
    standardId: "",
    sectionId: "",
    subjectId: "",
    diaryNote: "",
  });

  /* ---------------- > teacher mapping  */
  useEffect(() => {
    const fetchData = async () => {
      const res = await dispatch(getSubjectTeacher(teacherId)).unwrap();
      const data = res?.data || [];

      setRawData(data);

      const uniqueStandards = [
        ...new Map(
          data.map((i) => [i.standardId._id, i.standardId])
        ).values(),
      ];
      setStandards(uniqueStandards);
    };

    fetchData();
  }, [dispatch, teacherId]);

  /*  handlers < ---------------- */
  const handleStandardChange = (e) => {
    const standardId = e.target.value;

    setFormData({
      standardId,
      sectionId: "",
      subjectId: "",
      diaryNote: "",
    });

    const sec = rawData
      .filter((i) => i.standardId._id === standardId)
      .map((i) => i.sectionId);

    setSections([...new Map(sec.map((s) => [s._id, s])).values()]);
    setSubjects([]);
    setStudents([]);
    setSelectedStudents([]);
  };

// ---------- > handlesection 
  const handleSectionChange = async (e) => {
    const sectionId = e.target.value;

    setFormData((p) => ({
      ...p,
      sectionId,
      subjectId: "",
    }));

    const sub = rawData
      .filter(
        (i) =>
          i.standardId._id === formData.standardId &&
          i.sectionId._id === sectionId
      )
      .map((i) => i.subjectId);

    setSubjects([...new Map(sub.map((s) => [s._id, s])).values()]);

    setStudentLoading(true);
    try {
      const res = await dispatch(
        getStudent({
          standardId: formData.standardId,
          sectionId,
        })
      ).unwrap();

      setStudents(res?.data || []);
      setSelectedStudents([]);
    } catch {
      toast.error("Failed to fetch students");
    } finally {
      setStudentLoading(false);
    }
  };

  // handlestudent <---------------
  const handleStudentToggle = (id) => {
    setSelectedStudents((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  //---------- > handle select all students
  const handleSelectAllStudents = () => {
    setSelectedStudents(
      selectedStudents.length === students.length
        ? []
        : students.map((s) => s._id)
    );
  };

  //  handlechange <---------------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
    setErrors((p) => ({ ...p, [name]: "" }));
  };

  /* ---------------- > file helpers */
  const getFileIcon = (file) => {
    if (file.type === "application/pdf") return <MdFileCopy size={15}/>;
    if (file.type.startsWith("image/")) return <FcGallery size={15}/>;
    return "📎";
  };

  const handleRemoveFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files);

    if (files.length + selected.length > 3) {
      toast.error("You can upload maximum 3 files only");
      e.target.value = "";
      return;
    }

    const allowed = ["application/pdf", "image/jpeg", "image/png"];

    for (let f of selected) {
      if (!allowed.includes(f.type)) {
        toast.error("Only PDF, JPG, PNG allowed");
        e.target.value = "";
        return;
      }
      if (f.size > 2 * 1024 * 1024) {
        toast.error("Each file must be under 2MB");
        e.target.value = "";
        return;
      }
    }

    setFiles((prev) => [...prev, ...selected]);
    e.target.value = "";
  };

  const validateForm = () => {
    let err = {};
    Object.keys(requiredMessages).forEach((k) => {
      if (!formData[k]) err[k] = requiredMessages[k];
    });
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  /* ---------------- > submit  */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    if (!selectedStudents.length) {
      toast.error("Please select at least one student");
      return;
    }

    try {
      let uploadedFiles = [];

      for (let f of files) {
        const url = await uploadImage(f, token);
        uploadedFiles.push(url);
      }

      const payload = {
        standardId: formData.standardId,
        sectionId: formData.sectionId,
        teacherId,
        subjectId: formData.subjectId,
        diaryNote: formData.diaryNote,
        students: selectedStudents,
        files: uploadedFiles, 
        date: new Date().toISOString(),
      };

      console.log("payload",payload)

      await dispatch(adddiarynote(payload)).unwrap();
      toast.success("Diarynote added successfully");
      navigate("/teacher/diarynote");
    } catch {
      toast.error("Failed to add diary note");
    }
  };

  return (
    <Card className="an-card">
      <Card.Header className="an-header">
        <h5>Add Diarynote</h5>
      </Card.Header>

      <Card.Body>
        <Form onSubmit={handleSubmit}>
          <Row className="g-3">
            <Col md={4}>
              <Form.Label>Standard</Form.Label>
              <Form.Select
                value={formData.standardId}
                onChange={handleStandardChange}
              >
                <option value="">Select Standard</option>
                {standards.map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.name}
                  </option>
                ))}
              </Form.Select>
              {errors.standardId && (
                <small className="text-danger">{errors.standardId}</small>
              )}
            </Col>

            <Col md={4}>
              <Form.Label>Section</Form.Label>
              <Form.Select
                value={formData.sectionId}
                onChange={handleSectionChange}
                disabled={!sections.length}
              >
                <option value="">Select Section</option>
                {sections.map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.name}
                  </option>
                ))}
              </Form.Select>
              {errors.sectionId && (
                <small className="text-danger">{errors.sectionId}</small>
              )}
            </Col>

            <Col md={4}>
              <Form.Label>Subject</Form.Label>
              <Form.Select
                name="subjectId"
                value={formData.subjectId}
                onChange={handleChange}
                disabled={!subjects.length}
              >
                <option value="">Select Subject</option>
                {subjects.map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.name}
                  </option>
                ))}
              </Form.Select>
            </Col>

            {!studentLoading && students.length > 0 && (
              <Card className="mt-3">
                <Card.Header>
                  <Form.Check
                    type="checkbox"
                    label="Select All Students"
                    checked={selectedStudents.length === students.length}
                    onChange={handleSelectAllStudents}
                  />
                </Card.Header>
                <Card.Body style={{ maxHeight: 250, overflowY: "auto" }}>
                  {students.map((s) => (
                    <Form.Check
                      key={s._id}
                      type="checkbox"
                      label={`${s.name} (${s.rollNo || "-"})`}
                      checked={selectedStudents.includes(s._id)}
                      onChange={() => handleStudentToggle(s._id)}
                    />
                  ))}
                </Card.Body>
              </Card>
            )}

            <Col md={6}>
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                name="diaryNote"
                value={formData.diaryNote}
                onChange={handleChange}
                style={{height:"6rem",resize:"none"}}
              />
            </Col>

            {/* ---- > file upload with preview */}
            <Col md={6}>
              <Form.Label>Upload Files</Form.Label>

              {files.length > 0 && (
                <div className="mb-2 d-flex flex-row" >
                  {files.map((f, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        marginBottom: "6px",
                        backgroundColor:"whitesmoke",
                        borderRadius:"5px",
                      }}
                      className=" mx-1 p-1 d-flex align-items-center"
                      
                    >
                      <span style={{color:"#6f42c1"}}>{getFileIcon(f)}</span>
                      <span style={{fontWeight: 500, fontSize: "12px"}}>File {index + 1}</span>
                      <span
                        style={{ cursor: "pointer", color: "red", fontSize: "12px" }}
                        onClick={() => handleRemoveFile(index)}
                      >
                       <MdDelete size={15} />
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <Form.Control
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileChange}
                disabled={files.length >= 3}
              />
              <small className="text-muted">
                {files.length}/3 files 
              </small>
            </Col>
          </Row>

          <div className="an-actions mt-4">
            <button
              type="button"
              className="btn-cancel"
              onClick={() => navigate(-1)}
            >
              Cancel
            </button>
            <button type="submit" className="btn-submit">
              Submit
            </button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
}
